import { db } from '../lib/firebase';
import { collection, getDocs, query, where, doc, setDoc, increment, getDoc, serverTimestamp } from 'firebase/firestore';
import { updateDoc } from 'firebase/firestore';
import { createNotification } from './notifications';

export const getActiveStudentsCount = async (): Promise<number> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('isActive', '==', true));
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error fetching active students count:', error);
    return 0;
  }
};

export const getItemsSoldCount = async (): Promise<number> => {
  try {
    const listingsRef = collection(db, 'listings');
    const q = query(listingsRef, where('status', '==', 'sold'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error fetching items sold count:', error);
    return 0;
  }
};

export const getTotalSavings = async (): Promise<number> => {
  try {
    const listingsRef = collection(db, 'listings');
    const querySnapshot = await getDocs(listingsRef);
    let totalSavings = 0;
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.originalPrice && data.price) {
        totalSavings += (data.originalPrice - data.price);
      }
    });
    return totalSavings;
  } catch (error) {
    console.error('Error fetching total savings:', error);
    return 0;
  }
};

export const getAverageRating = async (): Promise<number> => {
  try {
    const reviewsRef = collection(db, 'reviews');
    const querySnapshot = await getDocs(reviewsRef);
    let totalRating = 0;
    let reviewCount = 0;
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.rating) {
        totalRating += data.rating;
        reviewCount++;
      }
    });
    return reviewCount > 0 ? (totalRating / reviewCount) : 0;
  } catch (error) {
    console.error('Error fetching average rating:', error);
    return 0;
  }
};

export const getVerifiedUsersCount = async (): Promise<number> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('isVerified', '==', true));
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error fetching verified users count:', error);
    return 0;
  }
};

export const getAverageResponseTime = async (): Promise<string> => {
  // This is a placeholder. Realistically, this would involve more complex logic
  // based on message timestamps and user activity. For now, return a static value.
  return '~5 min';
};

// Record a daily aggregated view for a listing
export async function recordListingView(listingId: string, sellerId: string) {
  const today = new Date();
  const key = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const docId = `${listingId}_${key}`;
  const ref = doc(db, 'listingViewsDaily', docId);
  await setDoc(ref, { listingId, sellerId, date: key, count: increment(1), updatedAt: Date.now() }, { merge: true });
}

// Get seller's daily views aggregated for the last N days
export async function getSellerDailyViews(sellerId: string, days: number): Promise<{ date: string; count: number }[]> {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - (days - 1));
  const startKey = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
  const col = collection(db, 'listingViewsDaily');
  const q = query(col, where('sellerId', '==', sellerId), where('date', '>=', startKey));
  const snapshot = await getDocs(q);
  const map = new Map<string, number>();
  // Seed last N days with 0
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    map.set(k, 0);
  }
  snapshot.forEach(docSnap => {
    const data = docSnap.data() as any;
    const date = data.date as string;
    const count = typeof data.count === 'number' ? data.count : 0;
    if (map.has(date)) map.set(date, (map.get(date) || 0) + count);
  });
  return Array.from(map.entries()).map(([date, count]) => ({ date, count }));
}

// Get a listing's daily views for the last N days
export async function getListingDailyViews(listingId: string, days: number): Promise<{ date: string; count: number }[]> {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - (days - 1));
  const startKey = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
  const col = collection(db, 'listingViewsDaily');
  const q = query(col, where('listingId', '==', listingId), where('date', '>=', startKey));
  const snapshot = await getDocs(q);
  const map = new Map<string, number>();
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    map.set(k, 0);
  }
  snapshot.forEach(docSnap => {
    const data = docSnap.data() as any;
    const date = data.date as string;
    const count = typeof data.count === 'number' ? data.count : 0;
    if (map.has(date)) map.set(date, (map.get(date) || 0) + count);
  });
  return Array.from(map.entries()).map(([date, count]) => ({ date, count }));
}

// Record a profile view for a seller (total + daily aggregation)
export async function recordSellerProfileView(sellerId: string) {
  const today = new Date();
  const key = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  // Daily aggregation
  const dailyId = `${sellerId}_${key}`;
  const dailyRef = doc(db, 'profileViewsDaily', dailyId);
  await setDoc(dailyRef, { sellerId, date: key, count: increment(1), updatedAt: Date.now() }, { merge: true });
  // Total count on user doc
  const userRef = doc(db, 'users', sellerId);
  try {
    await updateDoc(userRef, { profileViews: increment(1) });
  } catch (e) {
    // Fallback: ensure doc exists
    await setDoc(userRef, { profileViews: increment(1) }, { merge: true });
  }
}

// Get seller's daily profile views for the last N days
export async function getSellerDailyProfileViews(sellerId: string, days: number): Promise<{ date: string; count: number }[]> {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - (days - 1));
  const startKey = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
  const col = collection(db, 'profileViewsDaily');
  const q = query(col, where('sellerId', '==', sellerId), where('date', '>=', startKey));
  const snapshot = await getDocs(q);
  const map = new Map<string, number>();
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    map.set(k, 0);
  }
  snapshot.forEach(docSnap => {
    const data = docSnap.data() as any;
    const date = data.date as string;
    const count = typeof data.count === 'number' ? data.count : 0;
    if (map.has(date)) map.set(date, (map.get(date) || 0) + count);
  });
  return Array.from(map.entries()).map(([date, count]) => ({ date, count }));
}

// Record a unique viewer for a listing and notify seller on first view
export async function recordUniqueListingViewer(listingId: string, sellerId: string, viewerId: string): Promise<boolean> {
  if (!listingId || !sellerId || !viewerId) return false;
  try {
    const viewerRef = doc(db, 'listing_viewers', listingId, 'viewers', viewerId);
    const snap = await getDoc(viewerRef);
    if (snap.exists()) {
      return false; // already recorded; do not notify again
    }
    await setDoc(viewerRef, { viewerId, listingId, sellerId, createdAt: serverTimestamp() });
    try {
      await createNotification(sellerId, {
        type: 'view',
        title: 'New viewer on your listing',
        body: 'Someone viewed your product for the first time.',
        listingId,
        metadata: { viewerId }
      });
    } catch {}
    return true;
  } catch (e) {
    // swallow errors to avoid breaking product page
    return false;
  }
}